export class SearchModal {
  constructor() {
    this.initialize();
    this.observeDynamicElements();
  }

  private initialize(): void {
    const targetSelectors = [
      '.ist-header__row01NavLink[aria-label="検索"]',
      '.ist-footer__row02SearchLink[aria-label="検索"]',
      '.ist-header__row01SpNavLink[aria-label="検索"]',
      '.ist-header__row01NavLink[aria-label="Search"]',
      '.ist-footer__row02SearchLink[aria-label="Search"]',
      '.ist-header__row01SpNavLink[aria-label="Search"]',
    ];

    targetSelectors.forEach((selector) => {
      this.attachEventToTrigger(selector);
    });

    // モーダルがない場合は生成
    this.getOrCreateModal();
  }

  /**
   * モーダルが存在しない場合に生成し、モーダル要素を返す
   */
  private getOrCreateModal(): HTMLElement {
    let modalElement = document.getElementById('ist-modalSearch');

    if (!modalElement) {
      modalElement = document.createElement('div');
      modalElement.id = 'ist-modalSearch';
      modalElement.classList.add('ist-modalSearch');
      modalElement.setAttribute('role', 'dialog');
      modalElement.setAttribute('aria-label', '検索フォーム');
      modalElement.setAttribute('aria-hidden', 'true');

      modalElement.innerHTML = `
        <div class="ist-modalSearch__inner">
          <div class="ist-modalSearch__content">
            <form class="ist-modalSearch__form" action="">
              <div class="ist-modalSearch__contentRow ist-modalSearch__contentRow--01">
                <div class="ist-modalSearch__contentInput">
                  <input
                    type="text"
                    class="ist-modalSearch__contentInputText"
                    placeholder="サイト内検索"
                  />
                </div>
                <button type="submit" class="ist-modalSearch__contentButton">
                  <span class="ist-modalSearch__contentButtonText">検索</span>
                </button>
                <button
                  type="button"
                  class="ist-modalSearch__contentClose"
                  aria-label="閉じる"
                >
                  <i class="ist-icon ist-icon--close24"></i>
                </button>
              </div>
              <div class="ist-modalSearch__contentRow ist-modalSearch__contentRow--02">
                <a
                  href="/hcpro/"
                  class="ist-textLink ist-textLink--notUnderline ist-textLink--arrow"
                  >医療関係者さまはこちら</a
                >
              </div>
            </form>
          </div>
        </div>
      `;

      document.body.appendChild(modalElement);
    }

    return modalElement;
  }

  /**
   * 指定されたセレクタの要素にイベントを設定
   */
  private attachEventToTrigger(selector: string): void {
    const triggerElements = document.querySelectorAll(selector);

    triggerElements.forEach((triggerElement) => {
      const element = triggerElement as HTMLElement;
      if (element.getAttribute('data-initialized') === 'true') return; // 重複設定防止

      const modalId = 'ist-modalSearch';
      element.setAttribute('aria-controls', modalId);
      element.setAttribute('aria-expanded', 'false');
      element.setAttribute('data-initialized', 'true'); // 初期化済みフラグ

      element.addEventListener('click', (event) => {
        event.preventDefault();
        this.showSearchModal(modalId, element);
      });
    });
  }

  /**
   * 動的に生成されるHTML要素を監視し、初期化
   */
  private observeDynamicElements(): void {
    const observer = new MutationObserver(() => {
      this.initialize();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private showSearchModal(modalId: string, triggerElement: HTMLElement): void {
    const modalElement = this.getOrCreateModal();

    modalElement.setAttribute('aria-hidden', 'false');
    modalElement.classList.add('ist-modalSearch--open');

    // 15ms後にアニメーションクラスを追加
    setTimeout(() => {
      modalElement.classList.add('ist-modalSearch--animation');
    }, 15);

    const inputElement = modalElement.querySelector(
      '.ist-modalSearch__contentInputText'
    ) as HTMLInputElement | null;
    if (inputElement) {
      inputElement.focus();
    }

    const closeButton = modalElement.querySelector(
      '.ist-modalSearch__contentClose'
    ) as HTMLElement | null;
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeSearchModal(modalElement, triggerElement);
      });
    }

    modalElement.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeSearchModal(modalElement, triggerElement);
      }
    });

    // フォーカストラップの設定
    this.trapFocus(modalElement);

    triggerElement.setAttribute('aria-expanded', 'true');
  }

  private closeSearchModal(
    modalElement: HTMLElement,
    triggerElement: HTMLElement
  ): void {
    modalElement.classList.remove('ist-modalSearch--animation');

    // 15ms後に`--open`クラスを削除
    setTimeout(() => {
      modalElement.classList.remove('ist-modalSearch--open');
      modalElement.setAttribute('aria-hidden', 'true');
    }, 15);

    triggerElement.setAttribute('aria-expanded', 'false');
    triggerElement.focus();
  }

  private trapFocus(modalElement: HTMLElement): void {
    const focusableElements = modalElement.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement =
      focusableElements[focusableElements.length - 1];

    const handleFocusTrap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab: 前に戻る
        if (document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement.focus();
        }
      } else {
        // Tab: 次に進む
        if (document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      }
    };

    modalElement.addEventListener('keydown', handleFocusTrap);

    const observer = new MutationObserver(() => {
      if (modalElement.getAttribute('aria-hidden') === 'true') {
        modalElement.removeEventListener('keydown', handleFocusTrap);
        observer.disconnect();
      }
    });

    observer.observe(modalElement, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }
}
